import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { contractorService } from '../../../services/contractorService';
import { projectService } from '../../../services/projectService';
import { useBottomSheet } from '../../../hooks/useBottomSheet';

const AssignContractorModal = ({ isOpen, onClose, onSuccess, projectId }) => {
  const [formData, setFormData] = useState({
    contractorId: '',
    role: '',
    startDate: '',
    endDate: '',
    status: 'assigned',
    notes: ''
  });
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [error, setError] = useState('');

  const {
    sheetStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    scrollRef,
    showTopShadow,
    showBottomShadow,
    recheckShadows,
  } = useBottomSheet(onClose);

  useEffect(() => {
    if (isOpen) {
      loadContractors();
      const today = new Date()?.toISOString()?.split('T')?.[0];
      setFormData({
        contractorId: '',
        role: '',
        startDate: today,
        endDate: '',
        status: 'assigned',
        notes: ''
      });
      setError('');
    }
  }, [isOpen]);

  const loadContractors = async () => {
    setLoadingContractors(true);
    try {
      const data = await contractorService?.getAll();
      setContractors(data || []);
    } catch (error) {
      console.error('Error loading contractors:', error);
      setContractors([]);
    } finally {
      setLoadingContractors(false);
      recheckShadows();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name) => (value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!formData?.contractorId) {
        setError('Please select a contractor');
        setLoading(false);
        return;
      }
      await projectService?.assignContractor(projectId, formData?.contractorId, {
        role: formData?.role,
        startDate: formData?.startDate,
        endDate: formData?.endDate || null,
        status: formData?.status,
        notes: formData?.notes
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error assigning contractor:', error);
      setError(error?.message || 'Failed to assign contractor');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const contractorOptions = contractors?.map(c => ({
    value: c?.id,
    label: `${c?.name} - ${c?.role || 'Contractor'}`
  }));

  const statusOptions = [
    { value: 'assigned', label: 'Assigned' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on_hold', label: 'On Hold' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[300] p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-card w-full sm:rounded-xl sm:max-w-md shadow-2xl rounded-t-2xl max-h-[92vh] flex flex-col"
        style={{
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          ...(typeof window !== 'undefined' && window.innerWidth < 640 ? sheetStyle : {})
        }}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Enhanced drag handle on mobile */}
        <div
          className="sm:hidden flex-shrink-0 flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          <span className="text-[10px] text-muted-foreground/40 mt-1 font-medium tracking-wide">DRAG TO CLOSE</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-foreground">Assign Contractor</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Scroll indicator - top */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showTopShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to bottom, hsl(var(--card)) 0%, transparent 100%)', marginBottom: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Form - scrollable */}
        <form ref={scrollRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Contractor <span className="text-destructive">*</span>
            </label>
            <Select
              name="contractorId"
              value={formData?.contractorId}
              onChange={handleSelectChange('contractorId')}
              options={contractorOptions}
              placeholder={loadingContractors ? 'Loading contractors...' : 'Select contractor'}
              disabled={loadingContractors}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Role</label>
            <Input
              type="text"
              name="role"
              value={formData?.role}
              onChange={handleChange}
              placeholder="e.g., General Contractor, Electrician"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
              <Input type="date" name="startDate" value={formData?.startDate} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
              <Input type="date" name="endDate" value={formData?.endDate} onChange={handleChange} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <Select
              name="status"
              value={formData?.status}
              onChange={handleSelectChange('status')}
              options={statusOptions}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-base"
              placeholder="Additional notes about this assignment"
            />
          </div>
        </form>

        {/* Scroll indicator - bottom */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showBottomShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to top, hsl(var(--card)) 0%, transparent 100%)', marginTop: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Sticky footer actions */}
        <div className="flex gap-3 px-5 pt-3 border-t border-border flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 h-12"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 h-12"
            disabled={loading || loadingContractors}
            onClick={handleSubmit}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Icon name="Loader2" size={16} className="animate-spin" />
                Assigning...
              </span>
            ) : (
              'Assign Contractor'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignContractorModal;